/**
 * ContactForm Component
 * Feature: 016-client-contact-management - User Story 2
 * 
 * Form for creating and editing account contacts
 * Displays warning banner when adding contacts to archived accounts
 */

import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { FormInput } from '../shared/FormInput';
import { ChangeIndicator } from '../shared/ChangeIndicator';
import { useOptionalEditTracking } from '../../contexts/EditTrackingContext';
import {
  emailSchema,
  phoneSchema,
} from '../../utils/validation';

// Zod validation schema
const contactSchema = z.object({
  firstName: z.string().min(1, 'First name is required').max(100),
  lastName: z.string().min(1, 'Last name is required').max(100),
  email: emailSchema(false),
  phone: phoneSchema(false),
  role: z.string().max(100).optional().or(z.literal('')),
  idNumber: z.string().max(50).optional().or(z.literal('')),
  dateOfBirth: z.string().optional().or(z.literal('')),
  isEmployed: z.boolean().optional(),
});

type ContactFormSchema = z.infer<typeof contactSchema>;

export interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role?: string;
  idNumber?: string;
  dateOfBirth?: string;
  isEmployed?: boolean;
}

export interface ContactFormProps {
  onSubmit: (data: ContactFormData) => void | Promise<void>;
  onCancel?: () => void;
  defaultValues?: Partial<ContactFormData>;
  isLoading?: boolean;
  submitLabel?: string;
  mode?: 'create' | 'edit';
  accountStatus?: string;
  showArchivedWarning?: boolean;
}

export const ContactForm: React.FC<ContactFormProps> = ({
  onSubmit,
  onCancel,
  defaultValues,
  isLoading = false,
  submitLabel = 'Save Contact',
  mode = 'create',
  accountStatus,
  showArchivedWarning = false,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch,
  } = useForm<ContactFormSchema>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      firstName: defaultValues?.firstName || '',
      lastName: defaultValues?.lastName || '',
      email: defaultValues?.email || '',
      phone: defaultValues?.phone || '',
      role: defaultValues?.role || '',
      idNumber: defaultValues?.idNumber || '',
      dateOfBirth: defaultValues?.dateOfBirth || '',
      isEmployed: defaultValues?.isEmployed ?? true,
    },
  });

  const editTracking = useOptionalEditTracking();
  const watchedValues = watch();

  // Track field changes in edit mode
  useEffect(() => {
    if (mode === 'edit' && editTracking && defaultValues) {
      const fields: (keyof ContactFormData)[] = [
        'firstName', 'lastName', 'email', 'phone', 'role', 'idNumber', 'dateOfBirth', 'isEmployed'
      ];
      fields.forEach(field => {
        if (watchedValues[field] !== defaultValues[field]) {
          editTracking.markFieldChanged(field);
        }
      });
    }
  }, [watchedValues, defaultValues, mode, editTracking]);

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  const handleFormSubmit = (data: ContactFormSchema) => {
    onSubmit(data as ContactFormData);
  };

  const displayArchivedWarning = showArchivedWarning || accountStatus === 'NotActive';

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4">
      {/* Archived Account Warning */}
      {displayArchivedWarning && (
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-yellow-700">
                <strong>Warning:</strong> This contact is being added to an archived account. The account may need to be reactivated.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Name Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChangeIndicator isChanged={mode === 'edit' && editTracking?.changedFields.has('firstName')}>
          <FormInput
            label="First Name"
            id="firstName"
            type="text"
            required
            error={errors.firstName?.message}
            disabled={isLoading}
            {...register('firstName')}
          />
        </ChangeIndicator>

        <ChangeIndicator isChanged={mode === 'edit' && editTracking?.changedFields.has('lastName')}>
          <FormInput
            label="Last Name"
            id="lastName"
            type="text"
            required
            error={errors.lastName?.message}
            disabled={isLoading}
            {...register('lastName')}
          />
        </ChangeIndicator>
      </div>

      {/* Contact Info */}
      <ChangeIndicator isChanged={mode === 'edit' && editTracking?.changedFields.has('email')}>
        <FormInput
          label="Email"
          id="email"
          type="email"
          required
          error={errors.email?.message}
          disabled={isLoading}
          helperText="Must be unique across all contacts"
          {...register('email')}
        />
      </ChangeIndicator>

      <ChangeIndicator isChanged={mode === 'edit' && editTracking?.changedFields.has('phone')}>
        <FormInput
          label="Phone"
          id="phone"
          type="tel"
          required
          error={errors.phone?.message}
          disabled={isLoading}
          helperText="Must be unique across all contacts (e.g., +1 555-123-4567)"
          {...register('phone')}
        />
      </ChangeIndicator>

      {/* Optional Fields */}
      <ChangeIndicator isChanged={mode === 'edit' && editTracking?.changedFields.has('role')}>
        <FormInput
          label="Role"
          id="role"
          type="text"
          error={errors.role?.message}
          disabled={isLoading}
          helperText="Job title or position (optional)"
          {...register('role')}
        />
      </ChangeIndicator>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ChangeIndicator isChanged={mode === 'edit' && editTracking?.changedFields.has('idNumber')}>
          <FormInput
            label="ID Number"
            id="idNumber"
            type="text"
            error={errors.idNumber?.message}
            disabled={isLoading}
            helperText="Optional identification number"
            {...register('idNumber')}
          />
        </ChangeIndicator>

        <ChangeIndicator isChanged={mode === 'edit' && editTracking?.changedFields.has('dateOfBirth')}>
          <FormInput
            label="Date of Birth"
            id="dateOfBirth"
            type="date"
            error={errors.dateOfBirth?.message}
            disabled={isLoading}
            {...register('dateOfBirth')}
          />
        </ChangeIndicator>
      </div>

      {/* Employment Status */}
      <ChangeIndicator isChanged={mode === 'edit' && editTracking?.changedFields.has('isEmployed')}>
        <div className="flex items-center space-x-2">
          <input
            id="isEmployed"
            type="checkbox"
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            disabled={isLoading}
            {...register('isEmployed')}
          />
          <label htmlFor="isEmployed" className="text-sm font-medium text-gray-700">
            Currently Employed
          </label>
        </div>
        <p className="mt-1 text-sm text-gray-500">
          Indicates if this person is currently employed by the client company
        </p>
      </ChangeIndicator>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-4">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="submit"
          disabled={isLoading || (mode === 'edit' && !isDirty)}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : submitLabel}
        </button>
      </div>
    </form>
  );
};
